/**
 * @file: feedMessageDescriptor.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Centralized rendering metadata for every `FeedMessageType`. The inbox
 * row and the thread message card both call into here so we have ONE
 * place per type that produces:
 *
 *  - icon name + accent color (used for the leading avatar/badge)
 *  - short category label (used as a tag/chip)
 *  - human title + subtitle derived from the typed `params`
 *
 * Keep `descriptorFor` exhaustive — adding a new `FeedMessageType` should
 * surface a TS exhaustiveness error here at compile time.
 */

import { ReportContentType } from '@euler/model/ReportContentType';
import { ReportShareMethod } from '@euler/model/ReportShareMethod';
import { AbnormalLevel } from '@euler/model/enum/AbnormalLevel';
import { DeliveryCheckSceneType } from '@euler/model/enum/DeliveryCheckSceneType';
import { ReportShareTarget } from '@euler/model/enum/ReportShareTarget';
import { TaskFlowStatusType } from '@euler/model/enum/TaskFlowStatusType';
import { TaskFlowType } from '@euler/model/enum/TaskFlowType';
import { TaskStatusUpdateType } from '@euler/model/enum/TaskStatusUpdateType';
import {
  FeedMediaSnapshot,
  FeedMessagePayload,
  FeedMessageType,
} from '@euler/model/feed';
import { LeadIntentionLabel, LeadStatusLabel } from '@euler/model/lead/Lead';
import { formatVehicleBrandModel } from '@euler/utils/vehicle/brandModel';

export interface FeedMessageDescriptor {
  /** Icomoon icon name. */
  icon: string;
  /** Accent color for the icon's circular background. */
  accent: string;
  /** Short Chinese category label (e.g. "工单" / "维保计划"). */
  category: string;
  /** One-line title rendered on the row. */
  title: string;
  /** Secondary line — body / preview text. */
  subtitle?: string;
  /**
   * Optional preview-strip media. Currently produced by task-flow feed
   * payloads (`OrgTaskFlowStatusUpdate`). Frontend renders these as a
   * row of thumbnails to replace the bland fixed icon.
   */
  medias?: FeedMediaSnapshot[];
  /**
   * Optional color for the row's preview line, used to call attention to
   * exceptional events (e.g. red for order rollback). When unset, the
   * default neutral color applies.
   */
  previewAccent?: string;
}

const accents = {
  blue: '#1f80ff',
  green: '#34c759',
  amber: '#ff9500',
  red: '#ff3b30',
  purple: '#af52de',
  teal: '#0fb5ae',
  grey: '#8e8e93',
};

const reportTypeName = (t: ReportContentType): string => {
  switch (t) {
    case ReportContentType.PreInspection:
      return '预检报告';
    case ReportContentType.Inspection:
      return '检测报告';
    case ReportContentType.Construction:
      return '施工报告';
    case ReportContentType.DeliveryCheck:
      return '交车报告';
    case ReportContentType.SamplingCheck:
      return '抽检报告';
    default:
      return '检测报告';
  }
};

const shareMethodName = (m: ReportShareMethod): string => {
  switch (m) {
    case ReportShareMethod.WeixinMiniprogram:
      return '微信小程序';
    case ReportShareMethod.SmsLink:
      return '短信链接';
    case ReportShareMethod.WecomMiniprogram:
      return '企业微信小程序';
    case ReportShareMethod.AppLink:
      return '应用链接';
    default:
      return '其他方式';
  }
};

const shareTargetName = (t?: ReportShareTarget): string => {
  switch (t) {
    case ReportShareTarget.ServiceAgent:
      return '服务顾问';
    case ReportShareTarget.Customer:
    default:
      return '车主';
  }
};

const flowTypeName = (t: TaskFlowType): string => {
  switch (t) {
    case TaskFlowType.PreInspection:
      return '预检';
    case TaskFlowType.Inspection:
      return '检测';
    case TaskFlowType.InspectionFlow:
      return '检测流程';
    case TaskFlowType.Construction:
      return '施工';
    case TaskFlowType.ConstructionJob:
      return '施工任务';
    case TaskFlowType.CompletionCheck:
      return '交车';
    case TaskFlowType.SamplingCheck:
      return '抽检';
    default:
      return '工单流程';
  }
};

const flowStatusName = (s: TaskFlowStatusType): string => {
  switch (s) {
    case TaskFlowStatusType.Started:
      return '开始';
    case TaskFlowStatusType.Finished:
      return '完成';
    case TaskFlowStatusType.Cancelled:
      return '取消';
    default:
      return '更新';
  }
};

const taskStatusName = (s: TaskStatusUpdateType): string => {
  switch (s) {
    case TaskStatusUpdateType.Finished:
      return '已完成';
    case TaskStatusUpdateType.Rollback:
      return '回滚';
    case TaskStatusUpdateType.Cancelled:
      return '已取消';
    default:
      return '状态变更';
  }
};

const abnormalLevelName = (l: AbnormalLevel): string => {
  switch (l) {
    case AbnormalLevel.Urgent:
      return '紧急';
    case AbnormalLevel.Defective:
      return '异常';
    default:
      return '正常';
  }
};

const sceneTypeName = (s: DeliveryCheckSceneType): string => {
  // SceneType labels are best-effort fallbacks; the rework `title` is
  // usually carried explicitly by the producer.
  return `场景 #${s}`;
};

export function descriptorFor(
  payload: FeedMessagePayload,
): FeedMessageDescriptor {
  switch (payload.type) {
    case FeedMessageType.OrgNewOrder:
      return {
        icon: 'plus',
        accent: accents.blue,
        category: '新工单',
        title: `新建工单`,
        subtitle: [payload.customerName, payload.serviceSubjects]
          .filter(Boolean)
          .join(' · '),
      };

    case FeedMessageType.OrgOrderInfoUpdated: {
      const fields = Object.keys(payload.changes);
      return {
        icon: 'edit-2-o',
        accent: accents.amber,
        category: '工单信息',
        title:
          fields.length === 0
            ? '工单信息已更新'
            : `工单信息已更新 (${fields.length})`,
        subtitle: fields.join('、') || undefined,
      };
    }

    case FeedMessageType.OrgTaskAssigned:
      return {
        icon: 'user',
        accent: accents.teal,
        category: '任务分派',
        title: `${payload.technicianName} 接到任务${
          payload.taskName ? `「${payload.taskName}」` : ''
        }`,
        subtitle: payload.serviceSubjects,
      };

    case FeedMessageType.OrgTaskStatusUpdate: {
      const isRollback = payload.status === TaskStatusUpdateType.Rollback;
      return {
        icon: isRollback ? 'arrow-thin-left' : 'check',
        accent: isRollback ? accents.red : accents.green,
        category: isRollback ? '返单' : '工单状态',
        title: isRollback ? '返单' : `工单${taskStatusName(payload.status)}`,
        subtitle: payload.remark,
        previewAccent: isRollback ? accents.red : undefined,
      };
    }

    case FeedMessageType.OrgTaskFlowStatusUpdate:
      return {
        icon: 'arrow-thin-right',
        accent:
          payload.status === TaskFlowStatusType.Finished
            ? accents.green
            : payload.status === TaskFlowStatusType.Cancelled
            ? accents.grey
            : accents.blue,
        // Fold the status into the category chip itself so the body
        // can drop its TitleText — the chip already conveys "flow type
        // + status" (e.g. 施工任务开始) at a glance via its accent
        // color, no duplicate bold title needed below.
        category: `${flowTypeName(payload.refs.flowType)}${flowStatusName(
          payload.status,
        )}${payload.isRollback ? '（回滚）' : ''}`,
        title: `${flowTypeName(payload.refs.flowType)}${flowStatusName(
          payload.status,
        )}${payload.isRollback ? '（回滚）' : ''}`,
        subtitle: payload.refs.flowName ?? payload.remark,
        medias: payload.medias,
      };

    case FeedMessageType.OrgReworkRequest:
      return {
        icon: 'exclamation-circle-o',
        accent: accents.red,
        category: '返单',
        title: payload.title,
        subtitle: payload.responsibleMemberName
          ? `指派给 ${payload.responsibleMemberName}${
              payload.remarks ? ` · ${payload.remarks}` : ''
            }`
          : payload.remarks,
      };

    case FeedMessageType.OrgReportShared: {
      const sharer = payload.sharedByName ?? '同事';
      const method = shareMethodName(payload.shareMethod);
      const report = reportTypeName(payload.reportType);
      const target = shareTargetName(payload.shareTarget);
      // Mini-program shares fold the channel into the noun phrase ("分享了微信
      // 小程序检测报告"); every other channel reads as a means ("通过短信链接
      // 分享了检测报告").
      const isMiniprogram =
        payload.shareMethod === ReportShareMethod.WeixinMiniprogram ||
        payload.shareMethod === ReportShareMethod.WecomMiniprogram;
      return {
        icon: 'share',
        accent: accents.blue,
        category: '报告分享',
        title: isMiniprogram
          ? `${sharer}分享了${method}${report}给${target}`
          : `${sharer}通过${method}分享了${report}给${target}`,
      };
    }

    case FeedMessageType.OrgCustomerViewedReport:
      return {
        icon: 'search',
        accent: accents.purple,
        category: '车主查看',
        title: `${payload.customerName ?? '车主'} 查看了${reportTypeName(
          payload.reportType,
        )}`,
      };

    case FeedMessageType.OrgCustomerCommentPosted:
      return {
        icon: 'pencil',
        accent: accents.amber,
        category: '车主评价',
        title: `${payload.customerName ?? '车主'} 提交评价 ${payload.score}/5`,
        subtitle: payload.comment ?? undefined,
      };

    case FeedMessageType.OrgMaintenancePlanUpdated: {
      const d = payload.planDetail;
      const subjectName = d.customAlertSubjectName ?? d.name;
      return {
        icon: 'settings',
        accent: accents.blue,
        category: '维保计划',
        title: `新增维保项「${subjectName}」`,
        subtitle: `${abnormalLevelName(d.abnormalLevel)} · 下次保养 ${
          d.nextServiceDate
        }`,
      };
    }

    case FeedMessageType.OrgMaintenancePlanSettingsChanged: {
      const d = payload.planDetail;
      const subjectName = d ? d.customAlertSubjectName ?? d.name : '维保计划';
      const parts: string[] = [];
      if (payload.alertEnabled != null) {
        parts.push(`内部提醒${payload.alertEnabled ? '开启' : '关闭'}`);
      }
      if (payload.customerAlertEnabled != null) {
        parts.push(`车主提醒${payload.customerAlertEnabled ? '开启' : '关闭'}`);
      }
      return {
        icon: 'settings',
        accent: accents.grey,
        category: '维保设置',
        title: `${subjectName} 提醒设置更新`,
        subtitle: parts.join(' · '),
      };
    }

    case FeedMessageType.OrgMaintenancePlanRemind: {
      const d = payload.planDetail;
      const subjectName = d.customAlertSubjectName ?? d.name;
      const days = payload.daysUntilService;
      const dueText =
        days > 0
          ? `${days} 天后到期`
          : days === 0
          ? '今日到期'
          : `已过期 ${Math.abs(days)} 天`;
      // Preview: three clean lines — plate + vehicle, then the subject, then
      // the due info. Mileage / customer live on the card, not here.
      const vehicle = formatVehicleBrandModel(
        payload.vehicleBrandName,
        payload.vehicleModel,
      );
      const dueLine = [
        d.nextServiceDate ? `下次保养 ${d.nextServiceDate}` : undefined,
        dueText,
      ]
        .filter((s): s is string => !!s)
        .join(' · ');
      return {
        icon: 'notification',
        accent: days <= 1 ? accents.red : accents.amber,
        category: '保养提醒',
        title: [
          [payload.licensePlateNo, vehicle].filter(Boolean).join(' · '),
          subjectName,
          dueLine,
        ]
          .filter(Boolean)
          .join('\n'),
      };
    }

    case FeedMessageType.OrgMarketingFollowup:
      return {
        icon: 'notification',
        accent: accents.teal,
        category: '营销跟进',
        title: [payload.licensePlateNo, payload.campaignTitle]
          .filter(Boolean)
          .join(' · '),
        subtitle: payload.message,
      };
    case FeedMessageType.OrgLeadRemind: {
      // Preview: three clean lines — plate + vehicle, then
      // customer · status · intention, then the item list.
      const vehicle = formatVehicleBrandModel(
        payload.vehicleBrandName,
        payload.vehicleModel,
      );
      const itemsLine = payload.items.length
        ? payload.items
            .map(i => i.customLabel?.trim() || i.name)
            .filter((s): s is string => !!s)
            .join('、')
        : payload.subjectName;
      return {
        icon: 'notification',
        accent: accents.purple,
        category: '商机提醒',
        title: [
          [payload.licensePlateNo, vehicle].filter(Boolean).join(' · '),
          [
            payload.customerName,
            LeadStatusLabel[payload.status],
            LeadIntentionLabel[payload.intention],
            `目标 ${payload.targetServiceDate}`,
          ]
            .filter((s): s is string => !!s)
            .join(' · '),
          itemsLine,
        ]
          .filter(Boolean)
          .join('\n'),
      };
    }

    case FeedMessageType.OrgProductReferralRewardDeposited:
      return {
        icon: 'information-solid',
        accent: accents.green,
        category: '收益',
        title: `推广分润 +¥${formatYuan(payload.amount)}`,
        subtitle: `账户余额 ¥${formatYuan(payload.balance)}`,
      };

    case FeedMessageType.OrgCampaignRewardDeposited:
      return {
        icon: 'information-solid',
        accent: accents.amber,
        category: '收益',
        title: `活动奖励 +¥${formatYuan(payload.amount)}`,
        subtitle: `账户余额 ¥${formatYuan(payload.balance)}`,
      };

    case FeedMessageType.OrgMemberAccountWithdraw:
      return {
        icon: 'information-solid',
        accent: accents.grey,
        category: '提现',
        title: `提现 ¥${formatYuan(payload.amount)}`,
        subtitle: `账户余额 ¥${formatYuan(payload.balance)}`,
      };

    case FeedMessageType.OrgSystemAlert:
      return {
        icon: 'exclamation-circle-o',
        accent:
          payload.severity === 'error'
            ? accents.red
            : payload.severity === 'warning'
            ? accents.amber
            : accents.blue,
        category: '系统提醒',
        title: payload.title,
        subtitle: payload.message,
      };

    case FeedMessageType.OrgGlobalMessage:
      return {
        icon: 'information-solid',
        accent: accents.purple,
        category: '公告',
        title: payload.title,
        subtitle: payload.message,
      };

    default: {
      // Exhaustiveness guard — adding a new FeedMessageType without an
      // arm here is a TS error.
      const exhaustive: never = payload;
      void exhaustive;
      return {
        icon: 'notification',
        accent: accents.grey,
        category: '通知',
        title: '新消息',
      };
    }
  }
}

function formatYuan(cents: number): string {
  return (Math.round(cents) / 100).toFixed(2);
}

// Unused helper kept for future scene-aware rework labels.
void sceneTypeName;
